import _ from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'

import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import PageContainer from '@/components/ui/containers/PageContainer'

import { adminApi } from '@/core/api/admin'
import {
  useAssignLicensesToStaffMutation,
  useSetStaffSimplifiedReceptionModeMutation,
  useDeleteAdminStaffMutation,
  useGetAdminEstablishmentQuery,
  useLazyGetAdminStaffsQuery,
  useUpdateAdminEstablishmentMutation,
  useUpdateEstablismentLogoMutation,
} from '@/core/services/admin'
import { useGetContractByEstablishmentQuery } from '@/core/services/contract'
import { useGetLicensesQuery } from '@/core/services/license'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

import { getApiErrorMessage } from '@/utils/errors'
import { isAdmin } from '@/utils/type-guards'
import { FeatureFlag, useIsFlagEnabled } from '@/hooks/useIsFlagEnabled'
import { t as lt } from '@lingui/macro'
import { useLingui } from '@lingui/react'

import AssignLicenseDrawer from './components/AssignLicensesDrawer'
import DeleteConfirmationDialog from './components/DeleteStaffConfirmationDialog'
import PrivacyDialog from './components/PrivacyDialog'
import StaffsTable from './components/StaffsTable'
import AddFileConfirmationDialog from './components/AddLogoFile'
import logoImg from '@/assets/img/logo.png'
import { LoadingButton } from '@mui/lab'
import AddBackgroundContainer from './containers/AddBackgroundDialog'

const DEFAULT_OPTION = {
  limit: 5,
  skip: 0,
  orderBy: '',
  order: 'ASC' as 'ASC' | 'DESC',
}

export function AdminStaffsPage() {
  const dispatch = useAppDispatch()
  useLingui()
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const assignedLicenseIdRef = useRef<string>()

  const authUser = useAppSelector(selectAuthUser)

  const { data: establishment } = useGetAdminEstablishmentQuery()

  const { data: contract, refetch: refetchContract } = useGetContractByEstablishmentQuery(authUser?.establishment?._id ?? '', {
    skip: !isAdmin(authUser) || !authUser?.establishment,
  })

  const { data: licenses } = useGetLicensesQuery(
    { establishment: authUser?.establishment?._id },
    {
      skip: !isAdmin(authUser),
      refetchOnMountOrArgChange: true,
    }
  )

  const [getStaffs, { isFetching: isFetchingStaffs, staffs, originalArgs: getAdminStaffsArgs, totalStaffs }] = useLazyGetAdminStaffsQuery({
    selectFromResult: ({ data, ...rest }) => ({ staffs: data?.[0], totalStaffs: data?.[1], ...rest }),
  })

  const [assignLicensesToStaff, { isLoading: isAssigningLicense }] = useAssignLicensesToStaffMutation()
  const [deleteStaff, { isLoading: isDeletingStaff }] = useDeleteAdminStaffMutation()
  const [setSimplifiedReceptionMode] = useSetStaffSimplifiedReceptionModeMutation()
  const isSimplifiedReceptionFlagOn = useIsFlagEnabled(FeatureFlag.SimplifiedReceptionMode)
  const [updateEstablishment] = useUpdateAdminEstablishmentMutation()
  const [updateEstablishmentLogo, { isLoading: logoEstablishmentUpdate }] = useUpdateEstablismentLogoMutation()

  const [assignLicensesDrawerOpen, setAssignLicensesDrawerOpen] = useState(false)
  const [deleteStaffConfirmationDialogOpen, setDeleteStaffConfirmationDialogOpen] = useState<boolean>(false)
  const [privacyDialogOpen, setPrivacyDialogOpen] = useState<boolean>(false)
  const [selectedStaffId, setSelectedStaffId] = useState<string>()
  const [uploadDialogOpen, setUploadDialogOpen] = useState<boolean>(false)

  const [addBackgroundDialogOpen, setAddBackgroundDialogOpen] = useState(false)

  const privacyFormInitialValue = useMemo(() => {
    return {
      patientsPrivacy: establishment ? establishment.patientsPrivacy : '',
    }
  }, [establishment])

  const selectedStaff = useMemo(() => _.find(staffs, ['_id', selectedStaffId]), [staffs, selectedStaffId])

  const assignLicenses = useMemo(
    () =>
      licenses?.map((license) => {
        const licenseUsage = _.find(contract?.licenseUsages, ['license', license._id])
        const isAssigned = !!selectedStaff?.licenses.includes(license._id)
        const hasSeat = !!licenseUsage?.licensees.includes(selectedStaff?._id ?? '')
        return {
          id: license._id,
          isAssigned,
          isMissingSeat: isAssigned && !hasSeat,
          isAssigning: isAssigningLicense && assignedLicenseIdRef.current === license._id,
          name: license.name,
          description: license.description,
          policies: license.policies,
          availableQuantity: licenseUsage ? licenseUsage.quantity - licenseUsage.licensees.length : 0,
        }
      }) ?? [],
    [contract, isAssigningLicense, licenses, selectedStaff]
  )

  const staffsData = useMemo(() => {
    return _.map(staffs, (staff) => ({
      id: staff._id,
      name: staff.userInfo.name,
      surname: staff.userInfo.surname,
      gender: staff.userInfo.gender || 'I',
      city: staff.contactInfo?.city,
      cps: staff.cps,
      email: staff.email,
      status: staff.status,
      simplifiedReceptionMode: staff.simplifiedReceptionMode,
    }))
  }, [staffs])

  useEffect(() => {
    getStaffs({ filter: {}, options: DEFAULT_OPTION })
  }, [getStaffs])

  const handleSimplifiedReceptionModeChange = async (staffId: string, enabled: boolean) => {
    try {
      const result = await setSimplifiedReceptionMode({ staffId, enabled }).unwrap()
      if (getAdminStaffsArgs) {
        dispatch(
          adminApi.util.updateQueryData('getAdminStaffs', getAdminStaffsArgs, (draft) => {
            const staff = draft[0].find((item) => item._id === result._id)
            if (staff) staff.simplifiedReceptionMode = result.simplifiedReceptionMode
          })
        )
      }
      enqueueSnackbar(enabled ? lt`Simplified reception turned on` : lt`Simplified reception turned off`, { variant: 'success' })
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, lt`The simplified reception could not be changed`), { variant: 'error' })
    }
  }

  const handleUpload = async (file: File) => {
    if (!authUser?.establishment?._id) return
    try {
      setUploadDialogOpen(false)
      updateEstablishmentLogo({ establishmentId: authUser?.establishment._id, file })
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <PageContainer>
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <img alt="logo" src={establishment?.logo?.url ?? logoImg} height="48px" />
          <Typography variant="h2">{t('page.admin.staffs.title')}</Typography>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap="10px">
            <Button variant="contained" onClick={() => setAddBackgroundDialogOpen(true)}>
              {t('page.admin.background.dialog')}
            </Button>
            <LoadingButton
              onClick={() => setUploadDialogOpen(true)}
              loading={logoEstablishmentUpdate}
              disableElevation
              variant="outlined"
              id="patient-logo-button"
            >
              {t('page.admin.logo.dialogButton.label')}
            </LoadingButton>
            <Button onClick={() => setPrivacyDialogOpen(true)} disableElevation variant="contained" id="patient-privacy-button">
              {t('page.admin.privacy.dialogButton.label')}
            </Button>
            <Button component={Link} disableElevation to="/admin/staffs/create" variant="contained" id="add-staff-button">
              {t('page.admin.staffs.addStaffButton.label')}
            </Button>
          </Stack>
        </Stack>
        <StaffsTable
          data={staffsData}
          loading={isFetchingStaffs}
          onAssignLicensesClick={(staffId) => {
            setSelectedStaffId(staffId)
            setAssignLicensesDrawerOpen(true)
          }}
          onDeleteClick={(staffId) => {
            setSelectedStaffId(staffId)
            setDeleteStaffConfirmationDialogOpen(true)
          }}
          onSimplifiedReceptionModeChange={isSimplifiedReceptionFlagOn ? handleSimplifiedReceptionModeChange : undefined}
          onPaginationChange={({ pageIndex, pageSize }) => {
            getStaffs({ filter: {}, options: { ...DEFAULT_OPTION, limit: pageSize, skip: pageIndex * pageSize } })
          }}
          totalCount={totalStaffs ?? 0}
        />
      </Stack>
      <DeleteConfirmationDialog
        isDeleting={isDeletingStaff}
        isDoctor={selectedStaff?.status === 'MEDECIN'}
        name={selectedStaff?.userInfo.name ?? ''}
        surname={selectedStaff?.userInfo.surname ?? ''}
        open={deleteStaffConfirmationDialogOpen}
        onSubmit={async (values) => {
          if (!selectedStaffId) return

          try {
            await deleteStaff({ staffId: selectedStaffId, associatedData: values.associatedData }).unwrap()
            enqueueSnackbar(t('notification.staff.delete.success'), { variant: 'success' })
            setDeleteStaffConfirmationDialogOpen(false)
          } catch (error) {
            enqueueSnackbar(getApiErrorMessage(error, t('notification.staff.delete.error')), { variant: 'error' })
          }
        }}
        onClose={() => {
          setDeleteStaffConfirmationDialogOpen(false)
          setSelectedStaffId(undefined)
        }}
      />
      <PrivacyDialog
        initialValues={privacyFormInitialValue}
        open={privacyDialogOpen}
        onSubmit={async (values) => {
          if (!establishment) return

          try {
            await updateEstablishment({ establishmentId: establishment._id, ...values })
            setPrivacyDialogOpen(false)
          } catch (error) {
            enqueueSnackbar(getApiErrorMessage(error, t('notification.establishment.update.error')), { variant: 'error' })
          }
        }}
        onClose={() => setPrivacyDialogOpen(false)}
      />
      <AddFileConfirmationDialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(!uploadDialogOpen)} onUpload={handleUpload} />
      <AddBackgroundContainer open={addBackgroundDialogOpen} onClose={() => setAddBackgroundDialogOpen(false)} />
      <AssignLicenseDrawer
        licenses={assignLicenses}
        title={selectedStaff ? `${selectedStaff.userInfo.name} ${selectedStaff.userInfo.surname}` : ''}
        identifier={selectedStaff?.cps || selectedStaff?.email}
        open={assignLicensesDrawerOpen}
        onAssignLicense={async (licenseId, isAssigned) => {
          if (!selectedStaff) return

          try {
            assignedLicenseIdRef.current = licenseId
            const updatedStaff = await assignLicensesToStaff({
              staffId: selectedStaff._id,
              licenses: isAssigned ? _.concat(selectedStaff.licenses, licenseId) : _.without(selectedStaff.licenses, licenseId),
            }).unwrap()
            assignedLicenseIdRef.current = undefined
            if (getAdminStaffsArgs) {
              // pessimistic update to reflect change immediately
              dispatch(
                adminApi.util.updateQueryData('getAdminStaffs', getAdminStaffsArgs, (result) => {
                  const draffStaffs = result[0]
                  for (const draffStaff of draffStaffs) {
                    if (draffStaff._id !== updatedStaff._id) continue
                    Object.assign(draffStaff, updatedStaff)
                    break
                  }
                })
              )
            }
            refetchContract()
            enqueueSnackbar(isAssigned ? t('notification.license.assign.success') : t('notification.license.remove.success'), { variant: 'success' })
          } catch (error) {
            assignedLicenseIdRef.current = undefined
            enqueueSnackbar(getApiErrorMessage(error, t('notification.license.assign.error')), { variant: 'error' })
          }
        }}
        onClose={() => {
          setAssignLicensesDrawerOpen(false)
          setSelectedStaffId(undefined)
        }}
      />
    </PageContainer>
  )
}

export default AdminStaffsPage
